import notificationMessageList from 'utils/notificationMessageList';
import Services from 'services';
import RemoteConfiguration from 'remoteConfiguration/remoteConfiguration';
import { redirectTo } from 'components/common/navigation/redirect';
import Route from 'enum/route.enum';
import StorageKeys from 'enum/storages.enum';
import DeviceData from 'drivers/deviceData';
import EventBus from 'utils/eventBus';
import Storages from 'storages';

/**
 * Centralized notification handler
 * Processes all server notifications: acknowledgment, state changes, device actions
 * @param {string} data - Raw notification data from server
 * @param {function} onUIUpdate - Callback to display notification message (receives { message, isVisible })
 */
async function NotificationManager(data, onUIUpdate) {
  const parseData = JSON.parse(data);

  // Step 1: Acknowledge notification to server
  await acknowledgeNotification(parseData);

  // Step 2: Process notification-specific actions
  await processNotificationAction(parseData, onUIUpdate);

  // Step 3: Trigger refresh if needed
  await triggerRefreshIfNeeded(parseData);
}

/**
 * Send acknowledgment to server that notification was received
 */
async function acknowledgeNotification(parseData) {
  await Services.NotificationService.postNotificationMark({
    id: parseData.notification_id,
  }).then(
    (res) => {},
    (err) => {
      if (err.status === 500) {
        Storages.MainStorage.removeItem(StorageKeys.Keys.LICENSEAPIKEY);
        RemoteConfiguration.closeWebSocket();
        if (window.location.pathname !== Route.route.DEVICEIPNOTFOUND) {
          redirectTo(Route.route.DEVICEIPNOTFOUND);
        }
      }
    }
  );
}

/**
 * Execute device actions based on notification code
 */
async function processNotificationAction(parseData, notificationUI) {
  const { code, payload } = parseData;

  switch (code) {
    // Cache management
    case notificationMessageList.clearCache:
      RemoteConfiguration.clearCache([StorageKeys.Keys.ROOMDATA, StorageKeys.Keys.PROXYURL]);
      break;

    case notificationMessageList.deactivatePermanent:
      RemoteConfiguration.clearCache([StorageKeys.Keys.ROOMDATA]);
      RemoteConfiguration.closeWebSocket();
      break;

    // Device control actions
    case notificationMessageList.restart:
      await DeviceData.restartScreen();
      break;

    case notificationMessageList.turnOff:
      await DeviceData.turnOffScreen();
      break;

    // Volume control
    case notificationMessageList.volumeMuteOn:
      await DeviceData.volumeMuteOn();
      break;

    case notificationMessageList.volumeMuteOff:
      await DeviceData.volumeMuteOff();
      break;

    case notificationMessageList.setVolumeLevel:
      await DeviceData.setVolumeLevel();
      break;

    case notificationMessageList.unsetVolumeLevel:
      await DeviceData.unsetVolumeLevel();
      break;

    case notificationMessageList.volumeUp:
      await DeviceData.setVolumeUp();
      break;

    case notificationMessageList.volumeDown:
      await DeviceData.setVolumeDown();
      break;

    // Screenshot management
    case notificationMessageList.takeScreenShots:
      EventBus.publish('getScreenShots', '');
      break;

    case notificationMessageList.stopScreenShots:
      EventBus.publish('stopScreenShots', '');
      break;

    // Server changes
    case notificationMessageList.changeServer:
      if (notificationUI) {
        notificationUI({
          notification: 'Connecting to server.',
          isVisible: true,
        });
      }
      await RemoteConfiguration.cacheUpdate();
      break;

    // Customer data update
    case notificationMessageList.medicalEvent:
      RemoteConfiguration.updateGuestRegisteration(payload ?? parseData);
      break;

    // State changes (no immediate action needed)
    case notificationMessageList.activeDevice:
    case notificationMessageList.deactiveDevice:
    case notificationMessageList.deviceStatus:
    case notificationMessageList.updateCache:
    case notificationMessageList.locationChange:
      // Handled by refresh trigger below
      break;

    default:
      // Unknown code, silently ignore
      console.warn(`Unknown notification code: ${code}`);
  }
}

/**
 * Trigger app refresh for codes that require data reload
 */
async function triggerRefreshIfNeeded(parseData) {
  const refreshTriggerCodes = [
    notificationMessageList.updateCache,
    notificationMessageList.locationChange,
    notificationMessageList.activeDevice,
    notificationMessageList.deactiveDevice,
    notificationMessageList.clearCache,
    notificationMessageList.deactivatePermanent,
  ];

  if (refreshTriggerCodes.includes(parseData.code)) {
    // Calculate jitter if staggerWindowSeconds is provided
    const delay = parseData.staggerWindowSeconds
      ? Math.random() * (parseData.staggerWindowSeconds * 1000)
      : 0;

    setTimeout(() => {
      RemoteConfiguration.verifyDeviceAndInitialize().then(
        (res) => {},
        (err) => {
          console.error('Error during refresh trigger:', err);
        }
      );
    }, delay);
  }
}

const obj = { NotificationManager };

export default obj;
